// 插入资源包面板 — 列出本人资源包(走 v_resource_pack_mine),多选后单事务批量
// 插入 packRef 节点(只写 packId uuid,绝不写 url)。
//
// 数据源 useMyPacks → v_resource_pack_mine(owner 收口);仅出 active 包。

import { useMemo, useState } from 'react'
import { Spinner } from '@dogdisk/shared'
import { useMyPacks, type Pack } from '../../_lib/pack-api'

type InsertPackPanelProps = {
  open: boolean
  onClose: () => void
  /** 确认后回调选中的 pack id 列表(uuid),由编辑器 insertPackRefs 批量插入。 */
  onConfirm: (packIds: string[]) => void
}

export function InsertPackPanel({ open, onClose, onConfirm }: InsertPackPanelProps) {
  const { data: packs, isLoading } = useMyPacks()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const packOptions = useMemo(
    () => (packs ?? []).filter((p: Pack) => p.status === 'active'),
    [packs],
  )

  if (!open) return null

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const confirm = () => {
    onConfirm([...selected])
    setSelected(new Set())
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/50"
      data-testid="article-insert-pack-panel"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ maxHeight: '80vh', paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-2">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>
        <div className="flex items-center justify-between px-6 pb-2">
          <p className="text-[14px] font-semibold text-[#1A1A1A]">插入资源包</p>
          <button
            type="button"
            data-testid="article-insert-pack-confirm"
            onClick={confirm}
            disabled={selected.size === 0}
            className="text-[14px] text-[#19caad] disabled:opacity-40"
          >
            插入 {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="text-gray-400" />
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto px-6 pb-4">
            {packOptions.map((p) => (
              <li key={p.id}>
                <label className="flex items-center gap-3 rounded-lg p-3 active:bg-[#F6F7F8]">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                    className="h-4 w-4 accent-[#19caad]"
                  />
                  <span className="flex-1 truncate text-[14px]">{p.name}</span>
                  <span className="text-[11px] text-[#9CA3AF]">{p.price_coin ?? 0} COIN</span>
                </label>
              </li>
            ))}
            {packOptions.length === 0 && (
              <li className="py-8 text-center text-[12px] text-[#9CA3AF]">暂无资源包，先去创建</li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}
